/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box,  Checkbox, IconButton, List,  ListItem, ListItemText,  ListItemSecondaryAction, TextField, Typography,Divider } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { collection, addDoc, getDocs,  deleteDoc,  updateDoc,  doc,  query,  orderBy,  writeBatch } from "firebase/firestore";
import { db } from "../../firebase"; // Ajusta la ruta según tu proyecto
import { categorias } from "../../utils/categoriasCompra"; // Diccionario simple de categorías
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSliders, faTrash, faSortAlphaDown, faSortAlphaUp, faXmark, faCopy } from "@fortawesome/free-solid-svg-icons";
import { faCopy as faCopyRegular } from "@fortawesome/free-regular-svg-icons";
import { mostrarError, mostrarExito, mostrarInfo } from "../../utils/Alertas";
import { hideLoading, showLoading } from "../../../redux/actions/loadingActions";
import AppModal from "../secondary/Modal";
import { RootState } from "../../../redux/store/store";
import "./css/ListaCompra.css";

// Clasifica un producto por palabra clave
const clasificarProducto = (nombre: string): string => {
    if (!nombre) return "Otros";

    const nombreLower = nombre.toLowerCase();

    // 1) Coincidencia exacta (como antes)
    for (const [categoria, palabras] of Object.entries(categorias)) {
        if (palabras.some((w) => nombreLower.includes(w.toLowerCase()))) {
            return categoria;
        }
    }

    // 2) Coincidencia parcial inteligente
    for (const [categoria, palabras] of Object.entries(categorias)) {
        for (const palabra of palabras) {
            const palabraLower = palabra.toLowerCase();

            // Evitar coincidencias demasiado pequeñas como "a", "de", "el"
            if (palabraLower.length < 3) continue;

            // Si la palabra del diccionario contiene algo del nombre
            // o el nombre contiene parte de la palabra del diccionario
            if (
                palabraLower.includes(nombreLower) ||
                nombreLower.includes(palabraLower)
            ) {
                return categoria;
            }
        }
    }

    return "Otros";
};

const ListaCompra: React.FC = () => {
    const dispatch = useDispatch();
    const darkMode = useSelector((state: RootState) => state.theme.darkMode);
    const coleccionRef = collection(db, "lista_compra");

    const [productos, setProductos] = useState<any[]>([]);
    const [nuevoProducto, setNuevoProducto] = useState("");
    const [ordenAscendente, setOrdenAscendente] = useState(false);
    const [vistaPorSecciones, setVistaPorSecciones] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [copiado, setCopiado] = useState(false);

    // Cargar productos de Firestore
    const cargarProductos = async (usarLoading = true) => {
        if (usarLoading) dispatch(showLoading());

        try {
            const q = query(coleccionRef, orderBy("fechaCreacion", "asc"));
            const snapshot = await getDocs(q);

            let data = snapshot.docs.map((docu) => {
                const d = docu.data() as any;

                return {
                    id: docu.id,
                    nombre: d.nombre ?? "",
                    comprado: d.comprado ?? false,
                    seccion: d.seccion ?? clasificarProducto(d.nombre ?? ""),
                    fechaCreacion: d.fechaCreacion ?? new Date(),
                };
            });

            // Orden ascendente por defecto
            if (data.length > 0) {
                data = data.sort((a, b) => a.nombre.localeCompare(b.nombre));
            }

            setProductos(data);
        } catch (error) {
            console.error(error);
            mostrarError("Error al cargar la lista.");
        } finally {
            if (usarLoading) dispatch(hideLoading());
        }
    };

    useEffect(() => {
        cargarProductos();
    }, []);

    // Añadir producto individual o importación múltiple
    const agregarProducto = async (productoForzado?: string) => {
        const texto = productoForzado ?? nuevoProducto;
        if (!texto.trim()) return;

        try {
            // Detectar importación masiva
            const matches = texto.match(/- \[ \] ([^-]+)/g);
            if (matches && matches.length > 1) {
                await importarProductosMasivos(matches);
                setNuevoProducto("");
                return;
            }

            // Producto individual
            const nombreCapitalizado =
                texto.charAt(0).toUpperCase() + texto.slice(1);

            const seccion = clasificarProducto(nombreCapitalizado);

            const nuevoDoc = {
                nombre: nombreCapitalizado,
                comprado: false,
                seccion,
                fechaCreacion: new Date(),
            };

            // Primero añadimos a Firestore y recogemos el ID real
            const docRef = await addDoc(collection(db, "lista_compra"), nuevoDoc);

            const productoConID = {
                id: docRef.id,
                ...nuevoDoc,
            };

            setNuevoProducto("");

            if (vistaPorSecciones) {
                // AÑADIR SIEMPRE EL PRODUCTO CON ID
                setProductos((prev) => [...prev, productoConID]);
            } else {
                await cargarProductos(false);
            }

            mostrarExito("Producto añadido.");
        } catch (error) {
            console.error(error);
            mostrarError("No se pudo añadir el producto.");
        }
    };


    const cancelarOrdenacionSecciones = () => {
        setVistaPorSecciones(false);
    };

    const borrarTodaLaLista = async () => {
        dispatch(showLoading());
        try {
            const q = query(collection(db, "lista_compra"));
            const snapshot = await getDocs(q);

            const batch = writeBatch(db);
            snapshot.forEach((docu) => batch.delete(docu.ref));
            await batch.commit();

            mostrarExito("Lista borrada completamente.");
            await cargarProductos();
        } catch (error) {
            console.error(error);
            mostrarError("Error al borrar la lista.");
        } finally {
            setIsConfirmModalOpen(false);
            dispatch(hideLoading());
        }
    };

    // Importar muchos productos a la vez
    const importarProductosMasivos = async (matches: string[]) => {
        try {
            const productosLimpios = matches.map((m) =>
                m.replace(/- \[ \]\s*/g, "").trim()
            );

            const batch = writeBatch(db);

            productosLimpios.forEach((nombre) => {
                if (!nombre) return;
                const seccion = clasificarProducto(nombre);
                const nuevoRef = doc(collection(db, "lista_compra"));
                batch.set(nuevoRef, {
                    nombre,
                    comprado: false,
                    seccion,
                    fechaCreacion: new Date(),
                });
            });

            await batch.commit();
            setNuevoProducto("");

            mostrarExito(`Importados ${productosLimpios.length} productos.`);
            await cargarProductos(false);
        } catch (error) {
            console.error(error);
            mostrarError("Error al importar productos.");
        }
    };

    // Marcar como comprado
    const alternarComprado = async (id: string, comprado: boolean) => {
        const docRef = doc(db, "lista_compra", id);
        await updateDoc(docRef, { comprado: !comprado });
        cargarProductos(false);
    };

    // Eliminar producto
    const eliminarProducto = async (id: string) => {
        const docRef = doc(db, "lista_compra", id);
        await deleteDoc(docRef);
        cargarProductos(false);
    };

    // Orden alfabético
    const ordenarAlfabeticamente = () => {
        const ordenado = [...productos].sort((a, b) => {
            return ordenAscendente
                ? a.nombre.localeCompare(b.nombre)
                : b.nombre.localeCompare(a.nombre);
        });

        setProductos(ordenado);
        setOrdenAscendente(!ordenAscendente);
    };

    const copiarAlPortapapeles = async () => {
        const activos = productos
            .filter((p) => !p.comprado)
            .map((p) => p.nombre)
            .join("\n");

        if (!activos.trim()) {
            mostrarInfo("No hay productos activos para copiar.");
            return;
        }

        try {
            await navigator.clipboard.writeText(activos);
            mostrarExito("Lista copiada al portapapeles.");

            // Mostrar check ✔
            setCopiado(true);

            // Ocultar check después de 3 segundos
            setTimeout(() => setCopiado(false), 1000);
        } catch (error) {
            console.error(error);
            mostrarError("No se pudo copiar al portapapeles.");
        }
    };

    // Ordenar por secciones
    const ordenarPorSeccion = () => {
        setVistaPorSecciones(true);
    };

    const cancelDeleteReceta = () => {
        setIsConfirmModalOpen(false);
    };

    const handleEliminarReceta = async (event: React.MouseEvent<SVGSVGElement>) => {
        event.stopPropagation();
        setIsConfirmModalOpen(true);
    };

    // Agrupar productos por sección
    const productosAgrupados = productos.reduce((acc, p) => {
        const grupo = p.seccion ?? "Otros";
        if (!acc[grupo]) acc[grupo] = [];
        acc[grupo].push(p);
        return acc;
    }, {} as Record<string, any[]>);

    return (
        <div className="container-fluid lista-compra-page">
            <div className="text-center">
                <h2 className="pb-3">Lista de la Compra</h2>
            </div>

            <div className="container-fluid content pb-3">

                <TextField
                    label="Nuevo producto o pega aquí tu lista"
                    value={nuevoProducto}
                    onChange={(e) => setNuevoProducto(e.target.value)}
                    fullWidth
                    margin="normal"
                    style={{ marginTop: 0, marginBottom: 0}}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                        e.preventDefault();
                        if (nuevoProducto.trim() !== "") {
                            const capitalized =
                                nuevoProducto.charAt(0).toUpperCase() + nuevoProducto.slice(1);
                            agregarProducto(capitalized);
                            setNuevoProducto("");
                        }
                    }
                    }}
                    slotProps={{
                        input: {
                            autoCapitalize: "sentences",
                        },
                    }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5, mb: 1 }}>
                    Presiona "Intro" para añadir un producto.
                </Typography>

                <div className="row mb-1 align-items-center" style={{ height: "50px" }}>
                    {/* ORDENACIÓN A-Z */}
                    <div className="col text-center" onClick={ordenarAlfabeticamente} style={{ cursor: "pointer" }}>
                        <FontAwesomeIcon
                            icon={ordenAscendente ? faSortAlphaDown : faSortAlphaUp}
                            style={{ fontSize: "1.5rem", color: "#3a7ad1" }}
                        />
                    </div>

                    {/* SECCIONES o CANCELAR */}
                    <div
                        className="col text-center"
                        style={{
                            cursor: "pointer",
                            borderLeft: "1px solid black",
                            borderRight: "1px solid black",
                        }}
                        onClick={
                            vistaPorSecciones
                                ? cancelarOrdenacionSecciones
                                : ordenarPorSeccion
                        }
                    >
                        <FontAwesomeIcon
                            icon={vistaPorSecciones ? faXmark : faSliders}
                            style={{
                                fontSize: "1.5rem",
                                verticalAlign: "middle",
                                color: "#91d18b",
                            }}
                        />
                    </div>

                    {/* COPIAR AL PORTAPAPELES */}
                    <div
                        className="col text-center"
                        style={{
                            cursor: "pointer",
                            borderRight: "1px solid black",
                        }}
                        onClick={copiarAlPortapapeles}
                    >
                        <FontAwesomeIcon
                            icon={copiado ? faCopyRegular : faCopy}
                            style={{
                                fontSize: "1.5rem",
                                verticalAlign: "middle",
                                color: "#cfc64aff", // verde cuando se copia
                                transition: "all 0.2s ease",
                            }}
                        />
                    </div>


                    {/* BORRAR TODA LA LISTA */}
                    <div className="col text-center" onClick={(e: any) => handleEliminarReceta(e)} style={{ cursor: "pointer" }}>
                        <FontAwesomeIcon
                            icon={faTrash}
                            style={{ fontSize: "1.5rem", color: "#d13a3a" }}
                        />
                    </div>
                </div>

                {vistaPorSecciones  ? (
                    // AGRUPADA POR SECCIONES
                    <div className="secciones-grid">
                    {Object.keys(productosAgrupados).sort((a, b) => {
                            if (a === "Otros") return 1;   // Otros al final
                            if (b === "Otros") return -1;
                            return a.localeCompare(b);     // resto en orden A–Z
                        }).map((seccion) => (
                        <Box key={seccion} sx={{ mb: 0 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    mt: 1,
                                    backgroundColor: darkMode ? "#292929" : "#f5f5f5",
                                    px: 1,
                                    borderRadius: 1,
                                }}
                            >
                                <Typography variant="h6" sx={{ mt: 1, backgroundColor: darkMode ? "#292929" : "#f5f5f5", px: 1, borderRadius: 1 }}>
                                    {seccion === "PastaArroz" ? "Pasta y Arroz" : seccion === "FrutosSecos" ? "Frutos Secos" : seccion}
                                </Typography>
                            </Typography>
                            <Divider />
                            <List>
                                {productosAgrupados[seccion].map((p: any) => (
                                    <ListItem
                                        key={p.id}
                                        sx={{
                                            textDecoration: p.comprado
                                                ? "line-through"
                                                : "none",
                                            opacity: p.comprado ? 0.5 : 1,
                                            py: 0.5,
                                            px: 1.5
                                        }}
                                    >
                                        <Checkbox
                                            checked={p.comprado}
                                            onChange={() =>
                                                alternarComprado(p.id, p.comprado)
                                            }
                                        />
                                        <ListItemText primary={p.nombre} />
                                        <ListItemSecondaryAction>
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => eliminarProducto(p.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    ))}
                    </div>
                ) : (
                    // LISTA NORMAL SIN SECCIONES
                    <List>
                        {productos.map((p) => (
                            <ListItem
                                key={p.id}
                                sx={{
                                    textDecoration: p.comprado
                                        ? "line-through"
                                        : "none",
                                    opacity: p.comprado ? 0.5 : 1,
                                    py: 0.5,
                                    px: 1.5
                                }}
                            >
                                <Checkbox
                                    checked={p.comprado}
                                    onChange={() =>
                                        alternarComprado(p.id, p.comprado)
                                    }
                                />
                                <ListItemText primary={p.nombre} />
                                <ListItemSecondaryAction>
                                    <IconButton
                                        edge="end"
                                        aria-label="delete"
                                        onClick={() => eliminarProducto(p.id)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                )}

                {isConfirmModalOpen && (
                    <AppModal onClose={cancelDeleteReceta}>
                        <div className="modal-body">
                            <h2>Confirmar Eliminación</h2>
                            <p className="text-center">¿Estás seguro de que deseas eliminar toda la lista?</p>
                            <p>Esta acción no se puede deshacer.</p>
                            <div className="row">
                            <div className="col-6" style={{ paddingLeft: 0 }}>
                                    <button
                                        className="btn btn-secondary btn-block"
                                        onClick={cancelDeleteReceta}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                                <div className="col-6" style={{ paddingRight: 0 }}>
                                <button
                                        className="btn btn-danger btn-block"
                                        onClick={borrarTodaLaLista}
                                    >
                                        Eliminar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </AppModal>
                )}
            </div>
        </div>
    );
};

export default ListaCompra;
